import React, { useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useAlerts } from 'src/common/AlertManager';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useScheduleQuery, useScheduleRunsQuery } from 'src/api/queries/schedulesQueries';
import { useUpdateScheduleMutation, useDeleteScheduleMutation } from 'src/api/mutations/schedulesMutations';
import { runScheduleNow } from 'src/api/functions/scheduleRunsApi';
import { queryNames } from 'src/api/utils/queryNames';
import { useAiSchedulesUnavailable } from 'src/hooks/useAiSchedulesUnavailable';
import { useWritesPaused } from 'src/hooks/useWritesPaused';
import { PageTitle, Button, FeatureUnavailable, WritesPausedBanner } from 'src/components';
import { ScheduleStatusPill } from './components/ScheduleStatusPill';
import { SsePreviewPanel } from './components/SsePreviewPanel';
import { formatRecurrence, formatNextFire, formatDuration } from './utils';
import type { ScheduleStatus } from 'src/types/Schedules';
import styles from './ScheduleDetail.module.scss';

export const ScheduleDetail: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useAlerts();

  const { data: schedule, isLoading, error } = useScheduleQuery(id);
  const { data: runsData, isLoading: loadingRuns } = useScheduleRunsQuery(id);
  const [showRunPanel, setShowRunPanel] = useState(false);
  // Don't pass the per-schedule query error — a 404 here means the specific
  // schedule id wasn't found, which keeps its own handling below.
  const scheduleFeatureUnavailable = useAiSchedulesUnavailable();
  // Readable while the account is write-blocked; every action below would 503
  // (#1215), so they are disabled instead.
  const { paused: writesPaused, reason: pausedReason } = useWritesPaused();

  const updateMutation = useUpdateScheduleMutation(id, {
    onSuccess: (result) => {
      showSuccess(`Schedule ${result.status === 'active' ? 'resumed' : 'paused'}`);
    },
  });

  const deleteMutation = useDeleteScheduleMutation({
    onSuccess: () => {
      showSuccess('Agent deleted');
      history.push('/ai/schedules');
    },
  });

  const handlePauseResume = () => {
    if (!schedule) return;
    const newStatus: ScheduleStatus = schedule.status === 'active' ? 'paused' : 'active';
    updateMutation.mutate({ status: newStatus });
  };

  const runNowRequest = useCallback(
    (signal: AbortSignal) => runScheduleNow(accessToken, id, signal),
    [accessToken, id],
  );

  const handleRunDone = useCallback(() => {
    queryClient.invalidateQueries([queryNames.scheduleRuns, id]);
  }, [queryClient, id]);

  React.useEffect(() => {
    if ((error as any)?.status === 403) {
      showWarning('You no longer have access to this agent.');
      history.replace('/ai/schedules');
    }
  }, [error, showWarning, history]);

  const backButton = (
    <Button size="small" mode="tertiary" onClick={() => history.push('/ai/schedules')}>
      ← Scheduled agents
    </Button>
  );

  if (scheduleFeatureUnavailable) {
    return <FeatureUnavailable title="Scheduled agents" />;
  }

  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <PageTitle title="Scheduled agents" className={styles.pageTitle} rightContainer={backButton} />
        <div className={styles.pageContent}>
          <div className={styles.loading}>Loading…</div>
        </div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className={styles.pageWrapper}>
        <PageTitle title="Scheduled agents" className={styles.pageTitle} rightContainer={backButton} />
        <div className={styles.pageContent}>
          <div className={styles.emptyState}>Schedule not found.</div>
        </div>
      </div>
    );
  }

  const nf = formatNextFire(schedule.next_fire_at);
  const runs = runsData?.runs || [];
  const hasInvalidGrant = schedule.last_error === 'invalid_grant';

  return (
    <div className={styles.pageWrapper}>
      <PageTitle
        title="Scheduled agents"
        subtitle={schedule.title}
        className={styles.pageTitle}
        rightContainer={
          <div className={styles.titleActions}>
            <ScheduleStatusPill status={schedule.status} lastError={schedule.last_error} />
            {backButton}
          </div>
        }
      />

      <div className={styles.pageContent}>
        {writesPaused && <WritesPausedBanner reason={pausedReason} />}

        {schedule.consecutive_failures >= 1 && schedule.status === 'active' && (
          <div className={styles.failureWarning}>
            {schedule.consecutive_failures} consecutive failure{schedule.consecutive_failures > 1 ? 's' : ''}.
            {schedule.last_error && ` Last error: ${schedule.last_error}`}
            {' '}Auto-pause triggers at 3.
          </div>
        )}

        {schedule.status === 'paused' && schedule.last_error && (
          <div className={styles.pausedError}>
            <strong>Paused due to error:</strong> {schedule.last_error}
            {hasInvalidGrant ? (
              <Button
                size="small"
                mode="decline"
                outlined
                disabled={writesPaused}
                onClick={() => history.push(`/ai/schedules/authorize?return_to=/ai/schedules/${id}`)}
              >
                Re-authorize
              </Button>
            ) : (
              <Button size="small" mode="accept" outlined onClick={handlePauseResume} disabled={writesPaused}>
                Resume
              </Button>
            )}
          </div>
        )}

        <div className={styles.summaryGrid}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Recurrence</span>
            <span>{formatRecurrence(schedule.recurrence)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Timezone</span>
            <span>{schedule.timezone}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Next fire</span>
            <span title={nf.absolute}>{nf.absolute} ({nf.relative})</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Max duration</span>
            <span>{schedule.max_duration_s}s</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Destructive</span>
            <span>{schedule.allow_destructive ? 'Yes' : 'No'}</span>
          </div>
        </div>

        <div className={styles.promptSection}>
          <span className={styles.summaryLabel}>Prompt</span>
          <div className={styles.promptBox}>{schedule.prompt}</div>
        </div>

        <div className={styles.actions}>
          <Button size="small" onClick={() => setShowRunPanel(true)} disabled={writesPaused}>
            Run now
          </Button>
          {!hasInvalidGrant && (
            <Button
              size="small"
              mode="tertiary"
              onClick={handlePauseResume}
              disabled={writesPaused || updateMutation.isLoading}
            >
              {schedule.status === 'active' ? 'Pause' : 'Resume'}
            </Button>
          )}
          <Button
            size="small"
            mode="tertiary"
            disabled={writesPaused}
            onClick={() => history.push(`/ai/schedules/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            size="small"
            mode="decline"
            outlined
            disabled={writesPaused}
            onClick={() => {
              if (window.confirm(`Delete agent "${schedule.title}"?`)) {
                deleteMutation.mutate(id);
              }
            }}
          >
            Delete
          </Button>
        </div>

        {showRunPanel && (
          <div className={styles.runPanelWrapper}>
            <SsePreviewPanel
              title="Manual run"
              sendRequest={runNowRequest}
              onDone={handleRunDone}
              onClose={() => setShowRunPanel(false)}
            />
          </div>
        )}

        <div className={styles.runsSection}>
          <h2>Run history</h2>
          {loadingRuns ? (
            <div className={styles.loading}>Loading runs…</div>
          ) : runs.length === 0 ? (
            <div className={styles.emptyRuns}>
              No runs yet — fires next at {nf.absolute}.
            </div>
          ) : (
            <table className={styles.runsTable}>
              <thead>
                <tr>
                  <th>Fired at</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Tools</th>
                  <th>Credits</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.session_id}
                    className={styles.runRow}
                    onClick={() => history.push(`/ai/schedules/${id}/runs/${r.session_id}`)}
                  >
                    <td>{new Date(r.fired_at).toLocaleString()}</td>
                    <td>{formatDuration(r.duration_ms)}</td>
                    <td>
                      <span className={r.status === 'success' ? styles.runSuccess : styles.runFailure}>
                        {r.status}
                      </span>
                    </td>
                    <td>{r.tool_calls}</td>
                    <td>{r.credits.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
